import { Injectable, inject, signal } from '@angular/core';
import { IBaseEntity } from '@core/models/core';
import { IDType } from '@core/models/types';
import { DEFAULT_TASK_TIME } from '@core/performance';
import { ReferentialsController, SAP_ID } from '@core/referentials';
import { AppConfigService, LocationService } from '@core/services';
import { FeatureTypes, UserController } from '@core/user';
import { entityLabel } from '@entities/helpers';
import { EntityLike } from '@entities/models';
import { EntityType } from '@entities/types';
import { entityImageURI } from '@entity/helpers/ui';
import { BaseMixinService } from '@mixins/services';
import { TranslatableServiceMixin } from '@mixins/services/translatable';
import { isNullishArray } from '@utils/array';
import { µclearTimeout, µsetTimeout } from '@utils/function/timer';
import { isNullishNumber } from '@utils/number';
import { ToastMessageService } from '@widgets/toast-message';
import { WizardBasket, WizardBasketData, WizardBasketItem, WizardContainer } from '@wizard/models';
import { WizardBasketController } from '@wizard/services';
import { WizardBasketActions } from '@wizard/types';
import { WizardResultsController } from '@wizard/wizard-results/services';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, map, shareReplay } from 'rxjs';
import { WizardPanelListItemEditorData, WizardPanelListItemEditorOverlayService } from '../components';
import { WizardPanelCommentItemEvent } from '../events';
import { normalizeColumnType, normalizeWizardBasketItems } from '../helpers';
import { WizardItemEntity, isWizardItemEntity } from '../models';
import { AddItemDefaultOptions, IAddItemOptions, IRemoveItemOptions, RemoveItemDefaultOptions } from './models';
import { WizardPanelDialogService } from './wizard-panel-dialog.service';
import { BusinessEntity } from '@business-entities/business-entity';

@Injectable({ providedIn: 'root' })
export class WizardPanelController extends TranslatableServiceMixin(BaseMixinService) {

  /** @Inject */
  readonly #appConfigService = inject(AppConfigService);
  readonly #locationService = inject(LocationService);
  readonly #userController = inject(UserController);
  readonly #wizardBasketController = inject(WizardBasketController);
  readonly #wizardResultsController = inject(WizardResultsController);
  readonly #referentialsController = inject(ReferentialsController);
  readonly #overlayService = inject(WizardPanelListItemEditorOverlayService);
  readonly #toasterService = inject(ToastMessageService);
  readonly #dialogService = inject(WizardPanelDialogService);

  readonly #expanded = new BehaviorSubject<boolean>(false);
  // 🔥 hot observable
  readonly #expandedObs = this.#expanded.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #hideFooter = new BehaviorSubject<boolean>(true);
  // 🔥 hot observable
  readonly #hideFooterObs = this.#hideFooter.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #historyList = new BehaviorSubject<WizardItemEntity[]>([]);
  // 🔥 hot observable
  readonly #historyListObs = this.#historyList.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #dataListIds = new Set<IDType>();
  readonly #dataList = new BehaviorSubject<WizardItemEntity[]>([]);
  // 🔥 hot observable
  readonly #dataListObs = this.#dataList.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));
  readonly #stateChanged = new Subject<number>();
  // 🔥 hot observable
  readonly #stateChangedObs = this.#stateChanged.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly basket = signal<WizardBasket>(null);
  readonly isResultDisplayed = signal(false);

  readonly #updatedFromBroadcastChannel = new BehaviorSubject<boolean>(false);
  // 🔥 hot observable
  readonly updatedFromBroadcastChannel$ = this.#updatedFromBroadcastChannel.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly #updatedFromSettings = new BehaviorSubject<boolean>(false);
  // 🔥 hot observable
  readonly updatedFromSettings$ = this.#updatedFromSettings.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));

  #wizardResultsRoute: string;
  #basketChangesTimerId: number;

  constructor() {
    super();
    this.#wizardResultsRoute = this.#appConfigService.get('wizardResultsRoute');

    this.#locationService.locationChanged()
      .pipe(
        map(location => location.current),
        distinctUntilChanged(),
      ).subscribe(this.#onLocationChanged.bind(this));

    this.#observeBasketChanges();
    this.#onLocationChanged(this.#locationService.pathName);
  }

  get isExpanded$() { return this.#expandedObs; }
  get expanded(): boolean { return this.#expanded.getValue(); }
  get hideFooter$() { return this.#hideFooterObs; }
  get historyList$(): Observable<WizardItemEntity[]> { return this.#historyListObs; }
  get historyList(): WizardItemEntity[] { return this.#historyList.getValue(); }
  get dataList$(): Observable<WizardItemEntity[]> { return this.#dataListObs; }
  get dataList(): WizardItemEntity[] { return this.#dataList.getValue(); }
  get stateChanged$() { return this.#stateChangedObs; }
  // 🔥 hot observable - true is length === 0 or is null
  get isBasketEmpty$() { return this.#dataList.asObservable().pipe(map((data) => isNullishArray(data)), shareReplay({ bufferSize: 1, refCount: false })); }

  get canUseWizard(): boolean { return this.#userController.hasFeature(FeatureTypes.ReferentialCompanyWizard); }
  get dataListIsEmpty(): boolean { return isNullishArray(this.#dataList.getValue()); }
  get lastAddedItem(): WizardItemEntity { const added = this.#historyList.getValue(); return added[added.length - 1]; }
  get dataListCopy(): WizardItemEntity[] { return structuredClone(this.#dataList.getValue()); }

  // ⚛️ from WizardBasketController
  get isUserBasket$(): Observable<boolean> { return this.#wizardBasketController.isUserBasket$; }
  get isEditable$(): Observable<boolean> { return this.#wizardBasketController.isEditable$; }
  get editable(): boolean { return this.#wizardBasketController.editable; }
  get isLocked$(): Observable<boolean> { return this.#wizardBasketController.isLocked$; }
  get locked(): boolean { return this.#wizardBasketController.locked; }
  get isSaving$(): Observable<boolean> { return this.#wizardBasketController.isSavingBasket$; }
  get saving(): boolean { return this.#wizardBasketController.savingBasket; }
  get basketReferential(): IDType { return this.#wizardBasketController.basketReferential; }
  get basketReferential$(): Observable<IDType> { return this.#wizardBasketController.basketReferential$; }
  get basketVersion(): IDType { return this.#wizardBasketController.basketVersion; }
  get basketVersion$(): Observable<IDType> { return this.#wizardBasketController.basketVersion$; }
  get basketHasBeenChanged$(): Observable<boolean> { return this.#wizardBasketController.basketHasBeenChanged$; }

  toggleWizardPanel() {
    this.#expanded.next(!this.#expanded.getValue());
  }

  openWizardPanel() {
    if (!this.#expanded.getValue()) { this.#expanded.next(true); }
  }

  openWizardPanelIfNotEmpty() {
    if (!this.isEmpty() && !this.#expanded.getValue()) {
      this.#expanded.next(true);
    }
  }

  closeWizardPanel() {
    if (this.#expanded.getValue()) { this.#expanded.next(false); }
  }

  openOrCloseWizardPanel() {
    const isEmpty = this.isEmpty();
    const isExpanded = this.#expanded.getValue();

    if (isEmpty && isExpanded) {
      this.#expanded.next(false);
    }
    else if (!isEmpty && !isExpanded) {
      this.#expanded.next(true);
    }
  }

  /* ⚠️ this methode is super heavely called */
  hasEntity(entity: IBaseEntity): boolean {
    if (!entity) { return false; }
    const dataListIds = this.#dataListIds;
    if (dataListIds.size === 0) { return false; }
    return dataListIds.has(entity.id);
  }

  /* ⚠️ this methode is heavely called */
  hasEntityButPartially(entity: IBaseEntity): boolean {
    if (!entity) { return false; }
    const dataListIds = this.#dataListIds;
    if (dataListIds.size === 0) { return false; }
    const entityInList = this.getEntityById(entity.id);
    return dataListIds.has(entity.id) && !isNullishArray(entityInList?.items);
  }

  /* ⚠️ this methode is also heavely called */
  getEntityById(id: IDType): WizardItemEntity {
    const dataList = this.#dataList.getValue();
    const index = dataList.findIndex(data => data.id === id);
    return (index >= 0) ? dataList[index] : null;
  }

  async createNewCopy(): Promise<boolean> {
    return await this.#wizardBasketController.createNewCopy().then(basket => !!basket);
  }

  async newBasket(): Promise<boolean> {
    return this.#wizardBasketController.createNewBasket().then(basket => !!basket);
  }

  async addItem(item: WizardBasketItem | WizardItemEntity | BusinessEntity | EntityLike, addOptions: IAddItemOptions = {}): Promise<boolean> {
    return new Promise((resolve) => {
      const options = { ...AddItemDefaultOptions, ...addOptions };
      const { targetEntity, isColumn, isPureColumn } = getWizardItemIdentity(item);
      const history = options.history;
      const update = options.update;
      const historyList = history ? [...this.#historyList.getValue()] : null;
      const targetLabelAndName = entityLabel(targetEntity);
      let dataList = this.dataListCopy;
      let entity: WizardItemEntity;

      if (isColumn) {
        const parentId = isPureColumn ? targetEntity['objectId'] : targetEntity['parentId'];
        const parentIndex = dataList.findIndex(data => data.id === parentId);
        let labelAndName: string;
        let type: EntityType;
        let imageSrc: string;

        if (parentIndex >= 0) {
          const parentEntity = dataList[parentIndex];
          labelAndName = entityLabel(parentEntity);
          type = normalizeColumnType(parentEntity);
        }
        else {
          labelAndName = targetEntity['objectName'];
          type = targetEntity['objectType'];
        }

        imageSrc = entityImageURI({ type }, true, true);

        targetEntity.isColumn = true;
        targetEntity.label = entityLabel(targetEntity);

        entity = {
          id: parentId,
          label: labelAndName,
          name: labelAndName,
          imageSrc,
          type,
          referentialId: targetEntity.referentialId,
          versionId: targetEntity.versionId,
          items: [],
          isColumn: false,
          comment: null,
          timeStamp: performance.now()
        };
      }
      else {
        const type = normalizeColumnType(targetEntity);
        const targetImageSrc = entityImageURI({ type }, false, true);
        targetEntity.isColumn = false;
        entity = {
          ...targetEntity,
          type,
          label: targetLabelAndName,
          name: targetLabelAndName,
          imageSrc: targetImageSrc,
        };
      }

      if (this.hasEntity(entity)) {
        const findIndex = dataList.findIndex(data => data.id === entity.id);
        const entityData = dataList[findIndex];

        if (options.force && !isColumn) {
          if (entityData.items?.length) {
            entityData.items.forEach(item => this.#dataListIds.delete(item.id));
            entityData.items = [];
          }
        }
        else if (isColumn && !this.#dataListIds.has(targetEntity.id)) {
          entityData.items = [...entityData.items, targetEntity];
          this.#dataListIds.add(targetEntity.id);
        }

        if (history) { historyList.push(targetEntity); }

        dataList[findIndex] = structuredClone(entityData);
      }
      else {
        dataList.push({ ...entity, type: normalizeColumnType(entity), items: (isColumn ? [targetEntity] : []) as WizardItemEntity[] });
        this.#dataListIds.add(entity.id);
        this.#dataListIds.add(targetEntity.id);
        if (history) { historyList.push(entity); }
      }

      dataList = dataList.map(data => {
        const type = normalizeColumnType(data);
        const imageSrc = entityImageURI({ type }, true, true);
        const items = data.items?.map(item => {
          const itemImageSrc = entityImageURI(item, true, true);
          return {
            ...item,
            type: normalizeColumnType(item),
            imageSrc: itemImageSrc,
          };
        });

        return { ...data, items, type, imageSrc };
      });

      this.#dataList.next(dataList);

      if (this.#referentialsController.referential.id === SAP_ID) { this.#referentialsController.setReferentialToUserCompany(); }
      if (history) { this.#historyList.next(historyList); }
      if (update) { this.#updateAfterChanges(false); }

      this.#hideFooter.next(!dataList.length);
      this.openWizardPanel();

      return resolve(true);
    });
  }

  async removeItem(item: WizardBasketItem | WizardItemEntity | BusinessEntity | EntityLike, removeOptions: IRemoveItemOptions = {}): Promise<boolean> {
    const options = { ...RemoveItemDefaultOptions, ...removeOptions };

    return new Promise((resolve) => {
      const { targetEntity, isColumn } = getWizardItemIdentity(item);
      let hasBeenRemoved = false;
      let removeParent: WizardItemEntity = null;

      if (this.hasEntity(targetEntity)) {
        const dataList = this.dataListCopy;
        const targetId = targetEntity.id;
        const findIndex = dataList.findIndex(data => data.id === targetId);

        if (findIndex >= 0) {
          const removed = dataList.splice(findIndex, 1)[0];
          const dataItemIds = removed.items?.map(d => d.id);

          if (dataItemIds?.length) { dataItemIds.forEach(id => this.#dataListIds.delete(id)); }

          this.#dataListIds.delete(targetId);

          hasBeenRemoved = true;

        } else if (isColumn) {
          let index = -1;

          for (const data of dataList) {
            const dataItems = data.items;

            if (dataItems?.length >= 0) { index = dataItems.findIndex(d => d.id === targetId); }

            if (index >= 0) {
              const parentIndex = dataList.findIndex(d => d.id === data.id);
              const removedItem = data.items.splice(index, 1);

              this.#dataListIds.delete(removedItem[0].id);

              if (parentIndex >= 0) { dataList[parentIndex] = { ...data, items: [...data.items] }; }

              if (data.items.length === 0) {
                removeParent = { ...data, items: null };
                removeParent.items = null;
                dataList[parentIndex] = { ...data, items: [...data.items] };
              }

              hasBeenRemoved = true;
              break;
            }
          }
        }

        if (hasBeenRemoved) {
          this.#dataListIds.delete(targetId);
          this.#dataList.next([...dataList]);
          this.#updateAfterChanges();

          if (removeParent && !options.onlyItemsIfEntity) { this.removeItem(removeParent); }

          this.#hideFooter.next(!dataList.length);
        }
      }

      return resolve(hasBeenRemoved);
    });
  }

  async saveBasket(): Promise<WizardBasket> {
    return this.#wizardBasketController.saveBasket();
  }

  clearBasket() {
    this.#dialogService.clearBasketDialog().subscribe((data) => { if (data) { this.#finalyzeClearBasket(); } });
  }

  deleteBasket() {
    this.#dialogService.deleteBasketDialog().subscribe((data) => { if (data) { this.#finalyzeDeleteBasket(); } });
  }

  cancelLastAddition() {
    const historyList = [...this.#historyList.getValue()];
    const lastAdded = historyList.pop();

    if (lastAdded) {
      this.removeItem(lastAdded);
      this.#historyList.next(historyList);
    }
  }

  async updateBasketName(name: string) {
    await this.#wizardBasketController.updateBasketMeta({ name });
  }

  async updateBasketReferential(referentialId: IDType) {
    await this.#wizardBasketController.updateBasketMeta({ referentialId });
  }

  updateBasketVersion(versionId: IDType) {
    this.#wizardBasketController.updateBasketMeta({ versionId });
  }

  updateBWMigrationFlag(flag: boolean) {
    this.#wizardBasketController.updateBasketMeta({ bwMigration: flag });
  }

  isEmpty(): boolean {
    return this.#dataList.getValue()?.length === 0;
  }

  displayResults() {
    if (!this.#wizardBasketController.displayWizardBasketResults()) {
      this.#wizardBasketController.validateDisplayResults();
    }
  }

  refreshResults() {
    const updatedFromSettings = this.#updatedFromSettings.getValue();
    const updatedFromBroadcastChannel = this.#updatedFromBroadcastChannel.getValue();

    if (updatedFromBroadcastChannel) {
      this.#wizardBasketController.refreshBasketFromBroadcastChannel().then(this.#finalyseRefreshResult.bind(this));
    }
    else if (updatedFromSettings) {
      this.#finalyseRefreshResult(structuredClone(this.basket()));
    }
  }

  openCommentEditor(event: WizardPanelCommentItemEvent) {
    this.#overlayService.open(event.elementRef, {
      comment: event.item['comment'],
      mode: event.action
    }).subscribe((data) => this.#onWizardBasketItemEditorClosed(data, event));
  }

  #finalyseRefreshResult(basket: WizardBasket) {
    this.#parseBasket(basket);
    this.#wizardResultsController.reset();
    this.#wizardBasketController.validateDisplayResults('New');

    this.#updatedFromBroadcastChannel.next(false);
    this.#updatedFromSettings.next(false);
  }

  #updateAfterChanges(updateHistory = true) {
    if (updateHistory) { this.#checkHistory(); }
    this.#updateWizardBasketData().then(() => this.#fireUpdate());
  }

  #fireUpdate() {
    this.#stateChanged.next(performance.now());
  }

  #onWizardBasketItemEditorClosed(data: WizardPanelListItemEditorData, event: WizardPanelCommentItemEvent) {
    if (data) {
      const dataList = this.#dataList.getValue();
      const itemId = event.item.id;
      let findItemIndex = -1;
      const parentItemIndex = dataList.findIndex((node) => node.items.findIndex((item, index) => {
        if (item.id === itemId) {
          findItemIndex = index;
          return true;
        }
        return false;
      }) >= 0);

      if (parentItemIndex >= 0 && findItemIndex >= 0) {
        const parentItem = structuredClone(dataList[parentItemIndex]);
        const items = [...parentItem.items];

        items[findItemIndex].comment = data.comment;
        items[findItemIndex].timeStamp = performance.now();
        parentItem.items = [...items];
        parentItem.timeStamp = performance.now();
        dataList[parentItemIndex] = parentItem;

        this.#dataList.next([...dataList]);
        this.#updateWizardBasketData().then(() => this.#fireUpdate());
      }
    }
  }

  async #updateWizardBasketData() {
    await this.#wizardBasketController.updateBasketData(
      normalizeWizardBasketItems(
        this.#dataList.getValue()
      ),
      true
    );
  }

  #observeBasketChanges() {
    this.#wizardBasketController.basketChanges()
      .pipe(distinctUntilChanged())
      .subscribe(event => {
        const eventAction = event.action;
        const eventBasket = event.basket;

        if (!eventBasket) {
          this.#finalyzeClearBasket(false);
          this.basket.set(null);
        }
        else if (eventAction === WizardBasketActions.New) {
          this.#finalyzeClearBasket(false);
          this.basket.set(eventBasket);
        }
        else if (Boolean(eventAction)
          && (eventAction !== WizardBasketActions.Update && eventAction !== WizardBasketActions.Delete)
          || (eventAction === WizardBasketActions.Update && !this.basket())
        ) {
          this.basket.set(eventBasket);
        }

        // we need to only test last pasrt of the path, since Wizard home '/wizard/baskets/' will be includes !
        const wizardResultRouteFragment = (this.#appConfigService.get('wizardResultsRoute') as string).split('/').pop();
        const locationPathFragment = this.#locationService.pathName.split('/').pop();
        const weAreInWizardResults = wizardResultRouteFragment === locationPathFragment;
        if (weAreInWizardResults) {
          if (event.action === WizardBasketActions.UpdateFromOtherTab) {
            this.#updatedFromBroadcastChannel.next(true);
            this.#updatedFromSettings.next(false);
            this.#toasterService.show(this.translator.instant('Wizard.Panel.UI.Labels.BasketHasBeenModified'), 'Danger');
          } else if (event.action === WizardBasketActions.MetaChanged) {
            this.#updatedFromBroadcastChannel.next(false);
            this.#updatedFromSettings.next(true);
          }
        }
        µclearTimeout(this.#basketChangesTimerId);
        // FIXME maybe it is not a good beahiovr to change (parse) basket each times
        this.#basketChangesTimerId = µsetTimeout(() => this.#parseBasket(event.basket), DEFAULT_TASK_TIME);
      });
  }

  async #parseBasket(basket: WizardBasket, _update = false) {
    if (basket && basket.data) {
      Array.from(Object.entries(basket.data as WizardBasketData)).forEach((entries: [string, WizardContainer[]]) => {
        if (!isNullishArray(entries[1])) {
          entries[1].forEach(async entry => {
            if (!this.#dataListIds.has(entry.id)) {
              await this.addItem(entry, { history: false, update: false });

              if (entry.items?.length) {
                const parentId = entry.id;
                const parentName = entry.name;
                const versionId = entry.versionId;
                const referentialId = entry.referentialId;

                entry.items?.forEach(item => {
                  if (!this.#dataListIds.has(item.id)) {
                    this.addItem(
                      {
                        ...item,
                        parentId,
                        parentName,
                        versionId,
                        referentialId,
                        isColumn: true
                      } as EntityLike, { history: false, update: false }
                    );
                  }
                });
              }
            }
          });
        }
      });
    }
  }

  #finalyzeClearBasket(clearBasket = true) {
    if (clearBasket) { this.#wizardBasketController.clearBasket(true); }
    this.#dataList.next([]);
    this.#dataListIds.clear();
    this.#historyList.next([]);
    this.#checkHistory();
    this.#fireUpdate();
  }


  #finalyzeDeleteBasket() {
    this.#wizardBasketController.deleteBasket(true);
  }

  #checkHistory() {
    const dataList = this.#dataList.getValue();
    const hasItems = dataList?.length;

    if (!hasItems) {
      this.#historyList.next([]);
    }
    else {
      const historyList = [...this.#historyList.getValue()];

      if (historyList.length) {
        const dataIdList: IDType[] = dataList.map(d => d.id);
        const historyIdList: IDType[] = historyList.map(d => d.id);
        const toRemove: IDType[] = [];

        for (const itemId of historyIdList) {
          if (!dataIdList.includes(itemId)) { toRemove.push(itemId); }
        }

        if (toRemove.length) {
          const cleanedList = historyList.filter(item => !toRemove.includes(item.id));
          this.#historyList.next(cleanedList);
        }
      }
    }
  }

  #onLocationChanged(location: string) {
    const isResultRoute = location === this.#wizardResultsRoute;

    this.isResultDisplayed.set(isResultRoute);
  }
}

const getWizardItemIdentity = (item: WizardItemEntity | WizardBasketItem | BusinessEntity | EntityLike) => {
  const isPanelEntity = isWizardItemEntity(item);
  const isColumn = (
    ('objectId' in item) && !isNullishNumber(+item['objectId'])
    ||
    ('parentId' in item) && !isNullishNumber(+item['parentId'])
  );
  const isPureColumn = ('objectId' in item) && !isNullishNumber(+item['objectId']);
  const targetEntity: WizardItemEntity = structuredClone(item) as WizardItemEntity;

  return { isPanelEntity, targetEntity, isColumn, isPureColumn };
};
